/**
 * Animated Icon Component
 * Icon animations synchronized with counting and other animations
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor 
} from '../lib/animations';

// Animation types for icons
export type IconAnimationType = 
  | 'bounce' 
  | 'pulse' 
  | 'rotate' 
  | 'scale' 
  | 'shake' 
  | 'swing' 
  | 'flip' 
  | 'glow' 
  | 'float'
  | 'heartbeat'
  | 'wobble'
  | 'jello'
  | 'rubberBand'
  | 'tada'
  | 'flash';

// Configuration for icon animations
export interface IconAnimationConfig {
  type: IconAnimationType;
  duration?: number;
  delay?: number;
  repeat?: number | 'infinite';
  direction?: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  intensity?: 'subtle' | 'normal' | 'strong';
  trigger?: 'immediate' | 'hover' | 'click' | 'scroll' | 'sync';
  syncWithCounter?: boolean;
  counterValue?: number;
  counterTarget?: number;
}

interface AnimatedIconProps {
  icon: React.ComponentType<any>;
  config: IconAnimationConfig;
  className?: string;
  size?: number;
  color?: string;
  backgroundColor?: string;
  borderRadius?: number;
  padding?: number;
  onClick?: () => void;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
}

// Animation variants for different types
const createAnimationVariants = (
  type: IconAnimationType, 
  intensity: 'subtle' | 'normal' | 'strong' = 'normal'
) => {
  const intensityMultipliers = {
    subtle: 0.5,
    normal: 1,
    strong: 1.5
  };
  
  const multiplier = intensityMultipliers[intensity];

  switch (type) {
    case 'bounce':
      return {
        animate: {
          y: [0, -20 * multiplier, 0, -10 * multiplier, 0],
          transition: {
            duration: 0.8,
            ease: "easeOut"
          }
        }
      };

    case 'pulse':
      return {
        animate: {
          scale: [1, 1.1 * multiplier, 1],
          transition: {
            duration: 0.6,
            ease: "easeInOut"
          }
        }
      };

    case 'rotate':
      return {
        animate: {
          rotate: [0, 360 * multiplier],
          transition: {
            duration: 1,
            ease: "linear"
          }
        }
      };

    case 'scale':
      return {
        animate: {
          scale: [1, 1.2 * multiplier, 1],
          transition: {
            duration: 0.5,
            ease: "easeInOut"
          }
        }
      };

    case 'shake':
      return {
        animate: {
          x: [0, -10 * multiplier, 10 * multiplier, -10 * multiplier, 10 * multiplier, 0],
          transition: {
            duration: 0.6,
            ease: "easeInOut"
          }
        }
      };

    case 'swing':
      return {
        animate: {
          rotate: [0, 15 * multiplier, -10 * multiplier, 5 * multiplier, -5 * multiplier, 0],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'flip':
      return {
        animate: {
          rotateY: [0, 180 * multiplier],
          transition: {
            duration: 0.8,
            ease: "easeInOut"
          }
        }
      };

    case 'glow':
      return {
        animate: {
          filter: [
            'drop-shadow(0 0 0px currentColor)',
            `drop-shadow(0 0 ${10 * multiplier}px currentColor)`,
            'drop-shadow(0 0 0px currentColor)'
          ],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'float':
      return {
        animate: {
          y: [0, -8 * multiplier, 0],
          transition: {
            duration: 2,
            ease: "easeInOut"
          }
        }
      };

    case 'heartbeat':
      return {
        animate: {
          scale: [1, 1.3 * multiplier, 1, 1.3 * multiplier, 1],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'wobble':
      return {
        animate: {
          rotate: [0, -5 * multiplier, 3 * multiplier, -3 * multiplier, 2 * multiplier, -1 * multiplier, 0],
          x: [0, -25 * multiplier, 20 * multiplier, -15 * multiplier, 10 * multiplier, -5 * multiplier, 0],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'jello':
      return {
        animate: {
          skewX: [0, -12.5 * multiplier, 6.25 * multiplier, -3.125 * multiplier, 1.5625 * multiplier, 0],
          skewY: [0, -12.5 * multiplier, 6.25 * multiplier, -3.125 * multiplier, 1.5625 * multiplier, 0],
          transition: {
            duration: 0.9,
            ease: "easeInOut"
          }
        }
      };

    case 'rubberBand':
      return {
        animate: {
          scaleX: [1, 1.25 * multiplier, 0.75, 1.15 * multiplier, 0.95, 1.05 * multiplier, 1],
          scaleY: [1, 0.75, 1.25 * multiplier, 0.85, 1.05 * multiplier, 0.95, 1],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'tada':
      return {
        animate: {
          scale: [1, 0.9, 0.9, 1.1 * multiplier, 1.1 * multiplier, 1.1 * multiplier, 1.1 * multiplier, 1.1 * multiplier, 1.1 * multiplier, 1],
          rotate: [0, -3 * multiplier, -3 * multiplier, 3 * multiplier, -3 * multiplier, 3 * multiplier, -3 * multiplier, 3 * multiplier, -3 * multiplier, 0],
          transition: {
            duration: 1,
            ease: "easeInOut"
          }
        }
      };

    case 'flash':
      return {
        animate: {
          opacity: [1, 0, 1, 0, 1],
          transition: {
            duration: 0.75,
            ease: "easeInOut"
          }
        }
      };

    default:
      return {
        animate: {}
      };
  }
};

export const AnimatedIcon: React.FC<AnimatedIconProps> = ({
  icon: IconComponent,
  config,
  className = '',
  size = 24,
  color = 'currentColor',
  backgroundColor,
  borderRadius = 0,
  padding = 0,
  onClick,
  onAnimationStart,
  onAnimationComplete
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const { trackAnimation } = usePerformanceMonitor();

  // Create animation variants
  const variants = createAnimationVariants(config.type, config.intensity);

  // Handle counter synchronization
  useEffect(() => {
    if (config.syncWithCounter && config.counterValue !== undefined && config.counterTarget !== undefined) {
      const progress = config.counterValue / config.counterTarget;
      if (progress > 0.5 && !shouldAnimate) {
        setShouldAnimate(true);
      }
    }
  }, [config.syncWithCounter, config.counterValue, config.counterTarget, shouldAnimate]);

  // Handle trigger conditions
  useEffect(() => {
    switch (config.trigger) {
      case 'immediate':
        setShouldAnimate(true);
        break;
      case 'scroll':
        // This would be handled by parent component passing shouldAnimate
        break;
      default:
        break;
    }
  }, [config.trigger]);

  // Spring animation for smooth interactions
  const springProps = useSpring({
    scale: isHovered ? 1.1 : isClicked ? 0.95 : 1,
    config: {
      tension: 300,
      friction: 30
    },
    immediate: isReducedMotion
  });

  // Handle animation lifecycle
  const handleAnimationStart = () => {
    setIsAnimating(true);
    trackAnimation.start();
    if (onAnimationStart) onAnimationStart();
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    trackAnimation.end();
    if (onAnimationComplete) onAnimationComplete();
    
    // Reset for next animation if needed
    if (config.trigger === 'click') {
      setIsClicked(false);
      setShouldAnimate(false);
    }
  };

  // Handle click events
  const handleClick = () => {
    if (config.trigger === 'click') {
      setIsClicked(true);
      setShouldAnimate(true);
    }
    if (onClick) onClick();
  };

  // Determine if animation should play
  const shouldPlayAnimation = shouldAnimate && !isReducedMotion;

  // Container styles
  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size + (padding * 2),
    height: size + (padding * 2),
    backgroundColor,
    borderRadius,
    cursor: onClick ? 'pointer' : 'default',
    color
  };

  return (
    <animated.div
      style={{ ...springProps, ...containerStyle }}
      className={`${className} ${onClick ? 'select-none' : ''}`}
      onMouseEnter={() => config.trigger === 'hover' && setIsHovered(true)}
      onMouseLeave={() => config.trigger === 'hover' && setIsHovered(false)}
      onClick={handleClick}
    >
      <AnimatePresence>
        <motion.div
          key={`${config.type}-${shouldPlayAnimation}`}
          variants={variants}
          initial="initial"
          animate={shouldPlayAnimation ? "animate" : "initial"}
          transition={{
            duration: config.duration || variants.animate.transition?.duration || 1,
            delay: config.delay || 0,
            repeat: config.repeat === 'infinite' ? Infinity : (config.repeat || 0),
            repeatType: config.direction === 'alternate' ? 'reverse' : 'loop',
            repeatDelay: 0.1
          }}
          onAnimationStart={handleAnimationStart}
          onAnimationComplete={handleAnimationComplete}
        >
          <IconComponent size={size} />
        </motion.div>
      </AnimatePresence>
    </animated.div>
  );
};

// Preset configurations for common use cases
export const iconAnimationPresets = {
  countingSync: {
    type: 'pulse' as IconAnimationType,
    intensity: 'normal' as const,
    trigger: 'sync' as const,
    syncWithCounter: true,
    duration: 0.6,
    repeat: 2
  },
  
  achievement: {
    type: 'tada' as IconAnimationType,
    intensity: 'strong' as const,
    trigger: 'immediate' as const,
    duration: 1,
    delay: 0.5
  },
  
  interactive: {
    type: 'bounce' as IconAnimationType,
    intensity: 'subtle' as const,
    trigger: 'hover' as const,
    duration: 0.5
  },
  
  loading: {
    type: 'rotate' as IconAnimationType,
    intensity: 'normal' as const,
    trigger: 'immediate' as const,
    duration: 1,
    repeat: 'infinite' as const
  },
  
  notification: {
    type: 'shake' as IconAnimationType,
    intensity: 'normal' as const,
    trigger: 'immediate' as const,
    duration: 0.6,
    repeat: 3
  },
  
  success: {
    type: 'scale' as IconAnimationType,
    intensity: 'strong' as const,
    trigger: 'immediate' as const,
    duration: 0.5,
    delay: 0.2
  }
};

export default AnimatedIcon;